import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useOpenMemberProfile } from "@circle-react/hooks/profile/useOpenMemberProfile";
import { Avatar } from "@circleco/compass/components/Avatar";
import { Badge } from "@circleco/compass/components/Badge";
import type { MenuOptions } from "@circleco/compass/components/Menu";
import { Typography } from "@circleco/compass/components/Typography";

import { t } from "@/i18n-js/instance";
import { useBlockDms } from "@/react/hooks/chatsV2/blockDms/useBlockDms";
import type { ChatRoomParticipant } from "@/react/types/Chat/ChatRoomParticipant";

import { useMessageActions } from "../hooks/useMessageActions";
import { getAvatarStatus } from "./helper";
import { ParticipantActionsDropdownComponent } from "./ParticipantActionsDropdownComponent";
import type { ParticipantListProps } from "./types";

export const ParticipantListComponent = ({
  isViewOnlyMasquerading,
  onBlockMemberDm,
  onRemoveMember,
  onUnblockMemberDm,
  participantList,
}: ParticipantListProps) => {
  const showMemberProfile = useOpenMemberProfile();
  const { currentCommunityMember } = usePunditUserContext();
  const { canBlockMemberDm } = useBlockDms();
  const { shouldShowRemoveOption } = useMessageActions();

  const isCurrentCommunityMemberProfile = (member: ChatRoomParticipant) =>
    currentCommunityMember?.id === member.community_member_id;

  const getProfileActionItems = (member: ChatRoomParticipant): MenuOptions => {
    const isBlocked = member.is_blocked_by_current_community_member;
    const profileActionItems: MenuOptions = [
      {
        icon: "eye-open",
        label: t("view_profile"),
        onClick: () => showMemberProfile(member.user_public_uid || ""),
      },
    ];

    if (
      canBlockMemberDm({
        community_member_id: member.community_member_id,
        admin: member.community_admin,
      })
    ) {
      profileActionItems.push({
        icon: isBlocked ? "message-check" : "message-cross",
        label: t(
          isBlocked
            ? "messaging.unblock_direct_messages"
            : "messaging.block_direct_messages",
        ),
        onClick: () =>
          isBlocked ? onUnblockMemberDm(member) : onBlockMemberDm(member),
        danger: !isBlocked,
      });
    }

    if (
      shouldShowRemoveOption({
        community_member_id: member.community_member_id,
        messaging_enabled: Boolean(member.can_send_message_in_room),
      })
    ) {
      profileActionItems.push({
        icon: "cross",
        label: t("remove"),
        onClick: () => onRemoveMember(member),
        danger: true,
      });
    }

    return profileActionItems;
  };

  return (
    <div className="flex flex-col gap-0.5 -mx-2">
      {participantList.map(member => (
        <button
          type="button"
          className="hover:bg-hover dark:hover:bg-primary group flex w-full cursor-pointer justify-between flex-row items-center rounded-2xl px-3 py-2 h-10 has-[[data-popup-open]]:bg-hover dark:has-[[data-popup-open]]:bg-primary"
          key={member?.id}
          onClick={() => showMemberProfile(member?.user_public_uid || "")}
        >
          <div className="flex w-[90%] items-center text-left">
            <div className="mr-3">
              <Avatar
                name={member?.name}
                src={member?.avatar_url}
                status={getAvatarStatus(member?.status)}
                size="sm"
              />
            </div>
            <Typography variant="label-sm" truncate>
              {isCurrentCommunityMemberProfile(member)
                ? t("messaging.you")
                : member?.name}
            </Typography>
            {member?.community_admin ? (
              <div className="ml-2">
                <Badge label={t("admin")} variant="secondary" />
              </div>
            ) : (
              member?.moderator && (
                <div className="ml-2">
                  <Badge label={t("moderator")} variant="secondary" />
                </div>
              )
            )}
          </div>
          {!isViewOnlyMasquerading && (
            <ParticipantActionsDropdownComponent
              options={getProfileActionItems(member)}
            />
          )}
        </button>
      ))}
    </div>
  );
};
